import { TextInput } from '@app/components/core/input/text/TextInput'
import {
  LocationMapPreview,
  type LocationGeocodeResult,
} from '@app/components/blocks/LocationMapPreview'
import { useCallback } from 'react'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import type { InitiativeAddressState } from './initiativeAddressState'

export type InitiativeAddressInputProps = {
  state: InitiativeAddressState
  onStateChange: (
    state:
      | InitiativeAddressState
      | ((previous: InitiativeAddressState) => InitiativeAddressState),
  ) => void
  labelStyle?: Style
}

const emptyResolvedAddressRefs = () => ({})

export const InitiativeAddressInput = ({
  state,
  onStateChange,
  labelStyle,
}: InitiativeAddressInputProps) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const addressPlaceholder = `${translations.locationStreetPlaceholder} ${translations.locationPostcodePlaceholder}`

  const handleAddressChange = useCallback(
    (address: string) => {
      onStateChange((previous) => ({ ...previous, address }))
    },
    [onStateChange],
  )

  const handleGeocodeResult = useCallback(
    (result: LocationGeocodeResult | null) => {
      if (!result) {
        onStateChange((previous) => ({
          ...previous,
          resolvedAddress: '',
          geoCoords: null,
          resolvedAddressRefs: emptyResolvedAddressRefs(),
        }))
        return
      }

      onStateChange((previous) => ({
        ...previous,
        geoCoords: { lat: result.lat, long: result.long },
        resolvedAddress: result.displayName ?? '',
        resolvedAddressRefs: result.address ?? emptyResolvedAddressRefs(),
      }))
    },
    [onStateChange],
  )

  return (
    <>
      <TextInput
        label={translations.address}
        labelStyle={labelStyle}
        text={state.address}
        setText={handleAddressChange}
        placeholder={addressPlaceholder}
      />
      <LocationMapPreview
        address={state.address}
        onGeocodeResult={handleGeocodeResult}
      />
    </>
  )
}
